import useSEO from '@/hooks/useSEO';
import { Link } from 'react-router-dom';

const milestones = [
  { year: '2024 Q1', label: 'The idea', desc: 'Three forex traders, frustrated by how hard it was to compare real trading costs across brokers, started sketching what would become FXCostGuide.' },
  { year: '2024 Q3', label: 'First launch', desc: 'Published the first six cost guides — spread, commission, swap, slippage, hidden costs, and our step-by-step calculator. No marketing, just shared in a few trading communities.' },
  { year: '2025 Q1', label: 'Strategy deep-dive', desc: 'Added strategy-based cost breakdowns, showing scalpers, day traders, swing traders, and position traders exactly what each cost type means for their style.' },
  { year: '2025 Q4', label: 'Broker data layer', desc: 'Built a system to pull and cross-reference broker pricing data from public sources, updating spread and commission figures monthly.' },
  { year: 'Today', label: 'Where we are now', desc: 'Over 150,000 traders visit each month. We cover every major cost type with real numbers, real examples, and zero broker bias.' },
];

const team = [
  {
    name: 'Alex Kovacs',
    role: 'Content & Research',
    photo: '/images/team-alex.png',
    desc: 'Former prop trader who spent four years at a London-based forex desk. Has traded with 20+ brokers and knows exactly where they hide fees. Writes every guide with the same question in mind: "Would this have saved me money when I started?"',
  },
  {
    name: 'Maya Lindström',
    role: 'Data & Verification',
    photo: '/images/team-maya.png',
    desc: 'Quant background, previously built pricing models at a fintech startup. Runs the monthly broker data checks, cross-references spread figures, and makes sure every number on the site is traceable to a public source.',
  },
  {
    name: 'James Rivera',
    role: 'Product & Design',
    photo: '/images/team-james.png',
    desc: 'Full-stack developer and retail trader since 2019. Built the cost calculator, the broker comparison tools, and the entire site — driven by the belief that trading cost education should be free and easy to understand.',
  },
];

const organizationStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'FXCostGuide',
  url: import.meta.env.VITE_SITE_URL || 'https://fxcostguide.com',
  /* @replace:about-description */

  description: 'Meet the FXCostGuide team and learn how the site researches, checks and explains forex spreads, commissions, swaps, slippage and hidden trading costs.',
  foundingDate: '2024',
  founder: [
    {
      '@type': 'Person',
      name: 'Alex Kovacs',
      jobTitle: 'Content & Research',
      description: 'Former prop trader who spent four years at a London-based forex desk.',
    },
    {
      '@type': 'Person',
      name: 'Maya Lindström',
      jobTitle: 'Data & Verification',
      description: 'Quant background, previously built pricing models at a fintech startup.',
    },
    {
      '@type': 'Person',
      name: 'James Rivera',
      jobTitle: 'Product & Design',
      description: 'Full-stack developer and retail trader since 2019.',
    },
  ],
};

export default function AboutPage() {
  useSEO({
    /* @replace:about-title */

    title: 'About FXCostGuide: Our Team and Research Process',
    description: 'Meet the FXCostGuide team and learn how the site researches, checks and explains forex spreads, commissions, swaps, slippage and hidden trading costs.',
    /* @replace:about-keywords */

    keywords: 'FXCostGuide, forex trading cost research, forex education website, forex cost methodology, trading cost guides',
    /* @replace:about-canonical */

    canonical: '/about/',
  });

  return (
    <div className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationStructuredData) }}
      />

      {/* ════════════ 1. Hero ════════════ */}
      <section className="relative w-full overflow-hidden bg-foreground-950">
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: 'radial-gradient(circle, oklch(var(--primary-500)) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full opacity-[0.05] blur-[120px] bg-primary-500" />
        <div className="absolute -top-[120px] left-[10%] w-[360px] h-[360px] rounded-full border border-primary-500/6" />
        <div className="absolute -top-[80px] left-[12%] w-[260px] h-[260px] rounded-full border border-primary-500/4" />
        <div className="absolute -bottom-[100px] right-[8%] w-[340px] h-[340px] rounded-full border border-secondary-500/5" />
        <div className="absolute -bottom-[60px] right-[10%] w-[240px] h-[240px] rounded-full border border-secondary-500/3" />

        <div className="relative max-w-main mx-auto px-4 md:px-6 pt-16 md:pt-24 pb-16 md:pb-20 text-center">
          <p className="text-[13px] font-semibold tracking-[0.15em] uppercase text-primary-500 mb-5">
            About Us
          </p>
          <h1 className="text-[38px] md:text-[56px] font-[750] text-background-50 leading-[1.08] tracking-tight mb-5">
            The story behind
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">FXCostGuide</span>
              <span className="absolute left-0 right-0 bottom-[2px] h-[5px] rounded-full bg-primary-500/60" />
            </span>
          </h1>
          <p className="text-[16px] md:text-[18px] text-foreground-400 leading-relaxed max-w-[620px] mx-auto">
            We were tired of losing money to costs we didn&apos;t understand. So we built the resource we wish we&apos;d had from day one.
          </p>
        </div>
      </section>

      {/* ════════════ 2. Our Story ════════════ */}
      <section className="w-full bg-background-50 py-14 md:py-20">
        <div className="max-w-main mx-auto px-4 md:px-6">
          <div className="max-w-[720px] mx-auto text-center mb-12">
            <h2 className="text-[30px] md:text-[40px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
              How we got here
            </h2>
            <p className="text-[16px] leading-relaxed text-foreground-600">
              Three traders, one shared frustration, and a spreadsheet that turned into a website serving over 150,000 traders a month.
            </p>
          </div>

          {/* Timeline */}
          <div className="max-w-[680px] mx-auto relative">
            {/* Vertical line */}
            <div className="absolute left-[19px] md:left-[27px] top-2 bottom-2 w-[2px] bg-background-200" />

            <div className="flex flex-col gap-6 md:gap-8">
              {milestones.map((m, i) => (
                <div key={m.year} className="relative flex gap-5 md:gap-7 pl-0">
                  {/* Dot on the line */}
                  <div className="relative z-10 flex-shrink-0 w-10 h-10 md:w-14 md:h-14 rounded-full bg-background-50 border-2 border-background-200 flex items-center justify-center">
                    <div className={`w-3 h-3 md:w-3.5 md:h-3.5 rounded-full ${i === milestones.length - 1 ? 'bg-primary-500' : 'bg-foreground-300'}`} />
                  </div>
                  <div className="pt-1 md:pt-2.5">
                    <span className="inline-block text-[11px] md:text-[12px] font-bold tracking-[0.1em] uppercase text-primary-600 bg-primary-100 px-2.5 py-1 rounded-md mb-2">
                      {m.year}
                    </span>
                    <h3 className="text-[16px] md:text-[18px] font-bold text-foreground-950 mb-1.5">
                      {m.label}
                    </h3>
                    <p className="text-[14px] md:text-[15px] leading-relaxed text-foreground-600 max-w-[520px]">
                      {m.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ 3. Why We Built This ════════════ */}
      <section className="w-full bg-background-100 py-14 md:py-20">
        <div className="max-w-main mx-auto px-4 md:px-6">
          <div className="max-w-[720px] mx-auto">
            <h2 className="text-[30px] md:text-[40px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-5">
              Why we built this
            </h2>

            <div className="space-y-5">
              <p className="text-[16px] leading-relaxed text-foreground-600">
                Most forex traders lose money. Not because they make bad directional calls — but because they don&apos;t fully understand what they&apos;re paying on every trade. Spread. Commission. Swap. Slippage. Inactivity fees. Currency conversion markups. Each one chips away at your edge, and together they can turn what looks like a winning strategy into a net loss.
              </p>
              <p className="text-[16px] leading-relaxed text-foreground-600">
                When we started trading, we experienced this firsthand. We&apos;d calculate a trade, factor in the spread, and feel confident — only to see our account balance drift lower over weeks and months. The culprit? Costs we didn&apos;t know we were paying. Swap fees on positions held overnight. Slippage during news events. Hidden markups in the broker\'s conversion rate. Nobody was explaining this stuff in plain language, with real numbers.
              </p>

              {/* Stat highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 pt-8 border-t border-background-200">
                <div className="text-center">
                  <div className="text-[32px] md:text-[38px] font-[750] text-primary-600 leading-none mb-1">68%</div>
                  <p className="text-[13px] text-foreground-500 leading-snug">
                    of retail CFD accounts lose money each quarter (ESMA data, 2024–2025 average)
                  </p>
                </div>
                <div className="text-center">
                  <div className="text-[32px] md:text-[38px] font-[750] text-primary-600 leading-none mb-1">~35%</div>
                  <p className="text-[13px] text-foreground-500 leading-snug">
                    of a typical day trader&apos;s total costs come from spread alone
                  </p>
                </div>
                <div className="text-center">
                  <div className="text-[32px] md:text-[38px] font-[750] text-primary-600 leading-none mb-1">4&times;</div>
                  <p className="text-[13px] text-foreground-500 leading-snug">
                    how much more a high-cost broker can charge versus a low-cost one for the same trade
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ 4. Who&apos;s Behind This ════════════ */}
      <section className="w-full bg-background-50 py-14 md:py-20">
        <div className="max-w-main mx-auto px-4 md:px-6">
          <div className="max-w-[640px] mb-10 md:mb-14">
            <h2 className="text-[30px] md:text-[40px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
              Who&apos;s behind this
            </h2>
            <p className="text-[15px] leading-relaxed text-foreground-600">
              Not a big company. Not a broker. Just three traders who got frustrated, built a resource, and never stopped improving it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {team.map((person) => (
              <div key={person.name} className="group text-center">
                {/* Photo */}
                <div className="relative mx-auto mb-6 w-[132px] h-[132px] md:w-[152px] md:h-[152px]">
                  {/* Decorative ring */}
                  <div className="absolute inset-[-6px] rounded-full bg-gradient-to-b from-primary-500/20 via-transparent to-accent-500/15 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute inset-[-3px] rounded-full border-2 border-background-100" />
                  <img
                    src={person.photo}
                    alt={person.name}
                    className="relative w-full h-full rounded-full object-cover object-top grayscale-[15%] group-hover:grayscale-0 transition-all duration-500"
                    loading="lazy"
                  />
                </div>

                {/* Name */}
                <h3 className="text-[20px] md:text-[22px] font-[700] text-foreground-950 tracking-tight mb-1.5">
                  {person.name}
                </h3>

                {/* Role badge */}
                <span className="inline-block text-[11px] font-semibold tracking-[0.08em] uppercase text-primary-600 bg-primary-100/70 px-3 py-1 rounded-full mb-4">
                  {person.role}
                </span>

                {/* Description */}
                <p className="text-[14px] leading-relaxed text-foreground-600 max-w-[300px] mx-auto">
                  {person.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════ 5. How We Research ════════════ */}
      <section className="w-full bg-background-100 py-14 md:py-20">
        <div className="max-w-main mx-auto px-4 md:px-6">
          <div className="max-w-[720px] mx-auto">
            <h2 className="text-[30px] md:text-[40px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
              How we research &amp; verify
            </h2>
            <p className="text-[16px] leading-relaxed text-foreground-600 mb-12 md:mb-16">
              Every number you see on FXCostGuide is traceable to a public source. Here&apos;s how we keep the data accurate and current.
            </p>

            <div className="flex flex-col">
              {[
                {
                  num: '01',
                  title: 'Public data only',
                  accent: 'primary' as const,
                  desc: 'We pull spread, commission, and swap figures from publicly accessible broker websites and regulatory disclosure documents. No insider information, no privileged access — the same data any trader can verify themselves.',
                },
                {
                  num: '02',
                  title: 'Monthly update cycle',
                  accent: 'accent' as const,
                  desc: 'Brokers change their pricing. We run a full data refresh every month, cross-referencing figures across multiple sources. If a broker adjusts their spreads or fee structure, our numbers reflect it within 30 days.',
                },
                {
                  num: '03',
                  title: 'Cross-verification',
                  accent: 'secondary' as const,
                  desc: 'For any cost figure, we aim to confirm it against at least two independent sources. If numbers conflict, we flag the discrepancy and default to the more conservative figure so we never understate what a trader might pay.',
                },
                {
                  num: '04',
                  title: 'No black box math',
                  accent: 'primary' as const,
                  desc: 'Every formula and calculation is explained step by step on the site. You can reverse-engineer any cost figure and verify it yourself using your own broker\'s published rates.',
                },
              ].map((item, i) => (
                <div
                  key={item.num}
                  className={`group flex flex-col md:flex-row gap-5 md:gap-10 py-8 md:py-10 ${
                    i < 3 ? 'border-b border-background-200' : ''
                  }`}
                >
                  {/* Number */}
                  <div className="flex-shrink-0">
                    <span
                      className={`text-[52px] md:text-[64px] font-[750] leading-none tracking-[-0.03em] select-none ${
                        item.accent === 'primary'
                          ? 'text-primary-200'
                          : item.accent === 'accent'
                          ? 'text-accent-200'
                          : 'text-secondary-200'
                      } group-hover:text-primary-300 transition-colors duration-400`}
                    >
                      {item.num}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="pt-1">
                    <h3 className="text-[20px] md:text-[22px] font-[700] text-foreground-950 mb-2.5">
                      {item.title}
                    </h3>
                    <p className="text-[15px] leading-relaxed text-foreground-600 max-w-[560px]">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════ 6. CTA ════════════ */}
      <section className="w-full bg-foreground-950 py-14 md:py-20">
        <div className="max-w-main mx-auto px-4 md:px-6 text-center">
          <h2 className="text-[28px] md:text-[36px] font-[750] text-foreground-100 leading-[1.15] tracking-tight mb-4">
            Ready to see what your trades actually cost?
          </h2>
          <p className="text-[15px] text-foreground-400 max-w-[520px] mx-auto mb-8">
            We&apos;ve done the research. Now use it — start with the cost type that matters most to your trading style.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/forex-trading-costs-by-strategy/"
              className="px-6 py-3 bg-primary-500 text-background-50 rounded-btn text-[15px] font-semibold no-underline hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Costs by strategy
            </Link>
            <Link
              to="/calculate-forex-trading-costs/"
              className="px-6 py-3 bg-foreground-800 text-foreground-100 rounded-btn text-[15px] font-semibold no-underline hover:bg-foreground-700 transition-colors border border-foreground-700 whitespace-nowrap"
            >
              Calculate your cost
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}